import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {html} from 'lit';
import type StudioLink from './index.js';
import type {DatasetGroup} from '@gl/lit/types/datasets.js';
import type {HTTPResponse} from '@gl/lit/lib/http/immutable_fetcher.js';
import {wrapPromise} from '@gl/lit/lib/http/immutable_fetcher.js';
import {datasetGroupService} from '@gl/lit/services/dataset_group/index.js';
import {createDiscriminator} from '@gl/lit/utils/discriminator.js';
import {fold} from '@gl/lit/types/Either.js';
import {AsyncValueStatus} from '@gl/lit/types/async.js';
import {checkCurrentUrlForSegment} from '@gl/lit/utils/routes.js';
import {when} from 'lit/directives/when.js';
import type {SessionContextData} from '@gl/lit/contexts/session.js';

export type Model = {
  session?: SessionContextData;
  datasetGroups: DatasetGroup[];
  isModalOpen: boolean;
  status: AsyncValueStatus;
  isAuthorizedForSourceConnections: boolean;
};

export const Msg = makeVariants({
  setDatasetGroups: Variant<DatasetGroup[]>()('set-dataset-groups'),
  studioLinkClicked: Variant()('studio-link-clicked'),
  setIsModalOpen: Variant<boolean>()('set-is-modal-open'),
  setIsAuthorizedForSourceConnections: Variant<boolean>()(
    'set-is-authorized-for-source-connections'
  ),
  handleDatasetGroupsResponse: Variant<HTTPResponse<DatasetGroup[]>>()(
    'handle-dataset-group-response'
  ),
});

export type Msg = VariantsFor<typeof Msg>;

const discriminate = createDiscriminator<HTTPResponse<DatasetGroup[]>, Model>()(
  'kind'
);

function fetchDatasetGroups(host: StudioLink) {
  wrapPromise<DatasetGroup[]>(host, {
    promise: datasetGroupService.findAllDatasetGroups(),
    handler: Msg.handleDatasetGroupsResponse,
  });
}

function init(props: InitialProperties): Readonly<Model> {
  const session = <SessionContextData>props.get('session');
  return {
    session,
    datasetGroups: [],
    isModalOpen: false,
    status: AsyncValueStatus.IDLE,
    isAuthorizedForSourceConnections:
      <boolean>props.get('isAuthorizedForSourceConnections') ?? false,
  };
}

function handleLinkClick(host: StudioLink) {
  if (host.model.datasetGroups.length === 0) {
    location.href = '/audiences';
    return;
  }

  if (
    isSingleDatasetGroupWithMarveCondition(host.model.datasetGroups, true) ||
    !host.model.session?.enabled('marveEnabled')
  ) {
    location.href = `/v2/dataset_groups/${host.model.datasetGroups[0].id}/audience_studio`;
    return;
  }

  host.handleUpdate(Msg.setIsModalOpen(true));
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'studio-link-clicked': {
      return {
        ...model,
      };
    }
    case 'set-dataset-groups': {
      return {
        ...model,
        datasetGroups: msg.value,
      };
    }
    case 'set-is-modal-open': {
      return {
        ...model,
        isModalOpen: msg.value,
      };
    }
    case 'set-is-authorized-for-source-connections': {
      return {
        ...model,
        isAuthorizedForSourceConnections: msg.value,
      };
    }
    case 'handle-dataset-group-response': {
      return [
        discriminate(msg.value)({
          pending: () => ({...model, status: AsyncValueStatus.LOADING}),
          complete: ({response}) =>
            fold(
              () => {
                return {
                  ...model,
                  status: AsyncValueStatus.ERROR,
                };
              },
              (data: DatasetGroup[]) => {
                return {
                  ...model,
                  status: AsyncValueStatus.COMPLETE,
                  datasetGroups: data,
                };
              }
            )(response),
        }),
        cmd((host: StudioLink) => {
          if (host.model.status === AsyncValueStatus.COMPLETE) {
            handleLinkClick(host);
          }
        }),
      ];
    }
  }
}

function isSingleDatasetGroupWithMarveCondition(
  datasetGroups: DatasetGroup[],
  condition: boolean
) {
  return (
    datasetGroups.length === 1 && datasetGroups[0].marve_approval === condition
  );
}

function view(this: StudioLink, model: Model) {
  return html`
    <fly-list-item
      ?selected=${checkCurrentUrlForSegment('/audience_studio')}
      hasIcon
      large
      @click=${() => {
        fetchDatasetGroups(this);
      }}
    >
      ${when(
        model.status === AsyncValueStatus.LOADING,
        () =>
          html` <div class="studio-link__loader-container">
            <fly-loader loading></fly-loader>
          </div>`,
        () => html`
          <div slot="icon">
            <fly-icon icon="palette"></fly-icon>
          </div>
          Studio
        `
      )}
    </fly-list-item>
    ${when(
      this.model.isModalOpen,
      () => html`
        <fly-modal
          ?open=${this.model.isModalOpen}
          @closed=${() => {
            this.handleUpdate(Msg.setIsModalOpen(false));
          }}
          @opened=${() => {
            this.handleUpdate(Msg.setIsModalOpen(true));
          }}
          label="Dataset Group selection"
        >
          <dataset-selection-options
            ?isAuthorizedForSourceConnections=${model.isAuthorizedForSourceConnections}
            href=${'/audience_studio'}
            .datasetGroups=${model.datasetGroups}
          >
          </dataset-selection-options>
        </fly-modal>
      `
    )}
  `;
}

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
